/**
 * @file ChatFileUploader.tsx
 * @description Componente UI para subida de archivos en chat.
 * 
 * IMPLEMENTACIÓN TEMPORAL:
 * Interfaz simplificada integrada en el flujo de mensajería.
 * Ver README.md para detalles de implementación.
 */

import React, { type LegacyRef } from 'react';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { IconButton, Tooltip, Snackbar, Alert, useTheme, alpha } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { chatFileOptions, handleChatFiles, validateContextWindow, MAX_FILES } from './ChatFileHandler';

interface ChatFileUploaderProps {
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
  currentMessage: string;
  onValidationError?: (error: string | null) => void;
  selectedFiles: File[];
}

// El tope de ficheros sale de shared/uploadLimits.js (superficie `chat`), igual que el que
// aplican handleChatFiles, multer y el mensaje de error del backend.
//
// Antes se leía de REACT_APP_CHAT_MAX_FILES, que prometía configurabilidad y no la daba:
// handleChatFiles hardcodeaba 5 y el backend también, así que subir la variable sólo conseguía
// que la interfaz aceptara ficheros que el siguiente paso rechazaba (#439). Nunca se llegó a
// definir en ningún entorno.

export const ChatFileUploader: React.FC<ChatFileUploaderProps> = ({ onFilesChange, disabled, currentMessage, onValidationError, selectedFiles }) => {
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // NO limpiar el aviso cuando cambia el texto del input. El comentario que había aquí decía "si
  // cambia el mensaje o los archivos seleccionados", pero el efecto sólo miraba `currentMessage`:
  // una sola tecla mataba el aviso de "máx. 5 archivos", el usuario escribía su petición, la
  // enviaba SIN adjuntos —nada lo impide, ver useMessageSending.js— y el agente le volvía a pedir
  // la documentación. Este Snackbar es el ÚNICO canal del rechazo, así que al borrarlo no quedaba
  // rastro en pantalla (#1208, reporte de prod del 2026-09-14).
  //
  // El cierre ya está cubierto por tres vías que no dependen de teclear: una selección válida
  // (setErrorMessage(null) tras handleChatFiles), la X del Alert, y autoHideDuration.

  const {
    handleFileSelect,
    fileInputRef,
    error
  } = useFileUpload(
    async (newFiles) => {
      // Combinar archivos existentes y nuevos, evitando duplicados por nombre y tamaño
      const allFiles = [...selectedFiles, ...newFiles];
      const uniqueFiles: File[] = [];
      const seen = new Set();
      for (const file of allFiles) {
        const key = `${file.name}_${file.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueFiles.push(file);
        }
      }
      // Validar el total acumulado
      if (uniqueFiles.length > MAX_FILES) {
        const msg = `No puedes seleccionar más de ${MAX_FILES} archivos en total.`;
        setErrorMessage(msg);
        onValidationError && onValidationError(msg);
        return;
      }
      try {
        // Solo valida el mensaje: los adjuntos no consumen ventana de contexto (#444).
        await validateContextWindow(currentMessage);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Error desconocido al validar la ventana de contexto.';
        setErrorMessage(msg);
        onValidationError && onValidationError(msg);
        return;
      }
      try {
        await handleChatFiles(uniqueFiles);
        onFilesChange(uniqueFiles);
        setErrorMessage(null);
        onValidationError && onValidationError(null);
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Error al procesar archivos.';
        setErrorMessage(msg);
        onValidationError && onValidationError(msg);
      }
    },
    chatFileOptions
  );

  // Surface los errores de validación de TIPO/TAMAÑO lanzados DENTRO de handleFileSelect
  // (antes de que onFilesSelected corra) → hoy iban solo al console.error del onError por
  // defecto de chatFileOptions y el fichero se rechazaba en silencio. El hook ya expone
  // `error`; aquí lo cableamos al mismo Snackbar que el resto de errores del uploader.
  React.useEffect(() => {
    if (error) {
      setErrorMessage(error.message);
      onValidationError && onValidationError(error.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef as LegacyRef<HTMLInputElement>}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        multiple={chatFileOptions.multiple}
        accept={chatFileOptions.accept}
        // Límite de selección en la UI (no todos los navegadores lo respetan, por eso también se valida en JS)
        max={MAX_FILES}
      />
      <Tooltip title={`Adjuntar archivos (máx. ${MAX_FILES})`}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled}
            sx={{
              width: { xs: 32, md: 40 },
              height: { xs: 32, md: 40 },
              backgroundColor: { xs: 'transparent', md: theme.palette.background.paper },
              boxShadow: { xs: 'none', md: (theme as any).effects?.shadows?.brandMedium || '0 2px 8px rgba(34, 188, 202, 0.10)' },
              borderRadius: '50%',
              border: {
                xs: 'none',
                md: `1.2px solid ${alpha(theme.palette.primary.main, 0.2)}`
              },
              color: theme.palette.primary.main,
              transition: 'box-shadow 0.2s',
              mr: { xs: 0.5, md: 1 },
              ml: { xs: 0, md: 0.5 },
              '&:hover': {
                boxShadow: { xs: 'none', md: (theme as any).effects?.shadows?.brandStrong || '0 4px 16px rgba(34, 188, 202, 0.16)' },
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                borderColor: theme.palette.divider,
              }
            }}
          >
            <AddIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={(_event, reason) => {
          // Don't dismiss on accidental click-away — only on timer or explicit close
          if (reason === 'clickaway') return;
          setErrorMessage(null);
          onValidationError && onValidationError(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          sx={{ width: '100%' }}
          onClose={() => {
            setErrorMessage(null);
            onValidationError && onValidationError(null);
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}; 